// ==================== FILE: src/config/settingsService.js ====================
// Centralized access to init API settings (currency, timezone, OTP, delivery, etc.)
// All settings are stored in sessionStorage by brandingService.fetchInitData()

const SETTINGS_CACHE_KEY = 'resto_init_settings';

class SettingsService {
    constructor() {
        this._cache = null;
    }

    /**
     * Store settings data (called from brandingService.fetchInitData)
     */
    save(settings) {
        if (!settings) return;
        this._cache = settings;
        try {
            sessionStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(settings));
        } catch (e) {
            console.error('❌ Error saving settings:', e);
        }
    }

    /**
     * Retrieve all settings
     */
    getAll() {
        if (this._cache) return this._cache;
        try {
            const raw = sessionStorage.getItem(SETTINGS_CACHE_KEY);
            if (raw) {
                this._cache = JSON.parse(raw);
                return this._cache;
            }
        } catch (e) {
            console.error('❌ Error reading settings:', e);
        }
        return {};
    }

    // ─── Currency ────────────────────────────────────────────
    getCurrencySymbol() {
        return this.getAll().site_default_currency_symbol || '₹';
    }

    getCurrencyPosition() {
        // 5 = before amount (₹100), 10 = after amount (100₹)
        return this.getAll().site_currency_position || 5;
    }

    getDecimalPoints() {
        return parseInt(this.getAll().site_digit_after_decimal_point || '2', 10);
    }

    /**
     * Format a numeric amount with the correct currency symbol, position and decimals.
     * This is the SINGLE SOURCE OF TRUTH for price formatting.
     */
    formatPrice(amount) {
        const num = parseFloat(amount);
        if (!Number.isFinite(num)) {
            return `${this.getCurrencySymbol()}0`;
        }

        const decimals = this.getDecimalPoints();
        const formatted = num % 1 === 0 ? num.toFixed(0) : num.toFixed(decimals);
        const symbol = this.getCurrencySymbol();
        const position = this.getCurrencyPosition();

        // position 5 = symbol before, anything else = symbol after
        return position === 5
            ? `${symbol}${formatted}`
            : `${formatted}${symbol}`;
    }

    // ─── Google Maps ─────────────────────────────────────────
    getGoogleMapsKey() {
        return this.getAll().site_google_map_key || '';
    }

    // ─── Date/Time ───────────────────────────────────────────
    getDateFormat() {
        return this.getAll().site_date_format || 'd-m-Y';
    }

    getTimeFormat() {
        return this.getAll().site_time_format || 'h:i A';
    }

    getTimezone() {
        return this.getAll().site_default_timezone || 'Asia/Kolkata';
    }

    // ─── OTP ─────────────────────────────────────────────────
    getOtpDigitLimit() {
        return parseInt(this.getAll().otp_digit_limit || '4', 10);
    }

    getOtpExpireTime() {
        return parseInt(this.getAll().otp_expire_time || '20', 10);
    }

    // ─── Order Setup ─────────────────────────────────────────
    getFoodPreparationTime() {
        return parseInt(this.getAll().order_setup_food_preparation_time || this.getAll().site_food_preparation_time || '30', 10);
    }

    getScheduleSlotDuration() {
        return parseInt(this.getAll().order_setup_schedule_order_slot_duration || '30', 10);
    }

    getFreeDeliveryKilometer() {
        return parseFloat(this.getAll().order_setup_free_delivery_kilometer || '2');
    }

    getBasicDeliveryCharge() {
        return parseFloat(this.getAll().order_setup_basic_delivery_charge || '3');
    }

    getChargePerKilo() {
        return parseFloat(this.getAll().order_setup_charge_per_kilo || '0');
    }

    isTakeawayEnabled() {
        // 5 = enabled, 10 = disabled (based on API convention)
        // this.getAll().order_setup_takeaway === 5;
        return true;
    }

    isDeliveryEnabled() {
        //this.getAll().order_setup_delivery === 5;
        return true;
    }

    isInventoryCheck() {
        // Defaults to false if not provided
        return this.getAll().is_inventory_check === true;
    }

    isShiprocketEnabled() {
        return this.getAll().shiprocket_enabled === true;
    }

    // ─── Default Branch ──────────────────────────────────────
    getDefaultBranchId() {
        return this.getAll().site_default_branch || null;
    }

    // ─── Company Info ────────────────────────────────────────
    getCompanyName() {
        return this.getAll().company_name || '';
    }

    getCompanyPhone() {
        return this.getAll().company_phone || '';
    }

    getCompanyEmail() {
        return this.getAll().company_email || '';
    }

    getCompanyAddress() {
        const s = this.getAll();
        if (s.company_address) {
            return `${s.company_address}, ${s.company_city || ''} ${s.company_zip_code || ''}`.trim();
        }
        return '';
    }

    getCountryCode() {
        return this.getAll().company_country_code || 'IND';
    }

    // ─── Payment ─────────────────────────────────────────────
    isOnlinePaymentEnabled() {
        return this.getAll().site_online_payment_gateway === 5;
    }
}

// Export singleton
export const settingsService = new SettingsService();
export default settingsService;
